import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import api from '../utils/api';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, DollarSign, CheckCircle2 } from 'lucide-react';

const GenerateQuote = () => {
  const { prospectId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [prospect, setProspect] = useState(null);
  const [services, setServices] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [serviceQuantities, setServiceQuantities] = useState({}); // Per-service quantities
  const [selectedServices, setSelectedServices] = useState([]);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchData();
  }, [prospectId]);

  const fetchData = async () => {
    try {
      console.log('Fetching data for ID:', prospectId);
      
      // Try to fetch as prospect first, if that fails, try as client
      let prospectData = null;
      try {
        console.log('Trying to fetch as prospect...');
        const prospectRes = await api.get(`/prospects/${prospectId}`);
        console.log('Found as prospect:', prospectRes.data);
        prospectData = prospectRes.data;
      } catch (prospectError) {
        console.log('Not found as prospect, trying client...', prospectError.response?.status);
        // If prospect not found, try to fetch as client
        try {
          const clientRes = await api.get(`/clients/${prospectId}`);
          console.log('Found as client:', clientRes.data);
          prospectData = {
            id: clientRes.data.id,
            company_name: clientRes.data.company_name,
            contact_name: clientRes.data.contact_name || clientRes.data.company_name || '',
            contact_email: clientRes.data.contact_email || clientRes.data.email || '',
            address: clientRes.data.address || ''
          };
        } catch (clientError) {
          console.error('Not found as client either:', clientError.response?.status);
          throw new Error('Could not find prospect or client with ID: ' + prospectId);
        }
      }

      console.log('Fetching services and questions...');
      const [servicesRes, questionsRes] = await Promise.all([
        api.get('/pricing/services'),
        api.get('/pricing/questions')
      ]);

      console.log('Services:', servicesRes.data.services?.length || 0);
      console.log('Questions:', questionsRes.data.questions?.length || 0);

      setProspect(prospectData);
      setServices(servicesRes.data.services.filter(s => s.is_active));
      setQuestions(questionsRes.data.questions.filter(q => q.is_active).sort((a, b) => a.order - b.order));
      
      console.log('Data loaded successfully');
    } catch (error) {
      console.error('Failed to load data:', error);
      const errorMessage = error.message || error.response?.data?.detail || 'Failed to load data';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
    
    // Check if this question has linked services
    const question = questions.find(q => q.id === questionId);
    if (question && question.linked_services && question.linked_services.length > 0) {
      // For yes/no questions, only add services if answer is "Yes" or true
      const shouldAddServices = 
        question.question_type === 'yes_no' 
          ? (answer === 'Yes' || answer === true)
          : true;
      
      if (shouldAddServices) {
        const newServices = question.linked_services.filter(
          sid => !selectedServices.includes(sid)
        );
        setSelectedServices(prev => [...prev, ...newServices]);
      }
    }
  };

  const handleNext = () => {
    const currentQuestion = questions[currentStep];
    if (!answers[currentQuestion.id]) {
      toast.error('Please answer the question before continuing');
      return;
    }

    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Move to service selection
      setCurrentStep(questions.length);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const toggleService = (serviceId) => {
    setSelectedServices(prev => {
      const isCurrentlySelected = prev.includes(serviceId);
      if (isCurrentlySelected) {
        // Remove service
        const newQuantities = { ...serviceQuantities };
        delete newQuantities[serviceId];
        setServiceQuantities(newQuantities);
        return prev.filter(id => id !== serviceId);
      } else {
        // Add service with default quantity
        const service = services.find(s => s.id === serviceId);
        setServiceQuantities(prevQty => ({
          ...prevQty,
          [serviceId]: service?.min_users || 1
        }));
        return [...prev, serviceId];
      }
    });
  };

  const updateServiceQuantity = (serviceId, quantity) => {
    setServiceQuantities(prev => ({
      ...prev,
      [serviceId]: Math.max(1, parseInt(quantity) || 1)
    }));
  };

  const calculateTotal = () => {
    const servicesWithQuantity = selectedServices.map(sid => {
      const service = services.find(s => s.id === sid);
      if (!service) return null;
      
      const quantity = Math.max(serviceQuantities[sid] || service.min_users, service.min_users);
      const total = service.base_price * quantity;
      
      return {
        service_id: sid,
        service_name: service.name,
        base_price: service.base_price,
        quantity,
        total
      };
    }).filter(Boolean);

    const subtotal = servicesWithQuantity.reduce((sum, item) => sum + item.total, 0);
    
    return {
      servicesWithQuantity,
      subtotal,
      total: subtotal
    };
  };

  const handleGenerateQuote = async () => {
    if (selectedServices.length === 0) {
      toast.error('Please select at least one service');
      return;
    }

    setGenerating(true);
    try {
      const { servicesWithQuantity } = calculateTotal();
      
      // Calculate total number of users across all services for reference
      const totalUsers = Math.max(...servicesWithQuantity.map(s => s.quantity));
      
      const quoteData = {
        prospect_id: prospectId,
        services: servicesWithQuantity,
        num_users: totalUsers,
        discount_percent: 0,
        answers: answers,
        notes: null
      };

      const response = await api.post('/quotes', quoteData);
      console.log('Quote created:', response.data);
      
      toast.success('Quote generated successfully!');
      // Navigate to quote view page
      const quoteId = response.data.quote.id;
      console.log('Navigating to quote:', quoteId);
      navigate(`/quotes/${quoteId}`);
    } catch (error) {
      console.error('Failed to generate quote:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to generate quote: ${errorMessage}`);
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center">
        <p className="text-slate-500">Loading...</p>
      </div>
    );
  }

  const { servicesWithQuantity, subtotal, total } = calculateTotal();
  const isOnQuestionStep = currentStep < questions.length;
  const currentQuestion = isOnQuestionStep ? questions[currentStep] : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/prospects')}
                className="border-slate-300"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Prospects
              </Button>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-xl flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-slate-800">Generate Quote</h1>
                  <p className="text-sm text-slate-500">{prospect?.company_name}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between text-sm text-slate-600 mb-2">
            <span>Step {currentStep + 1} of {questions.length + 1}</span>
            <span>{Math.round(((currentStep + 1) / (questions.length + 1)) * 100)}% Complete</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-2">
            <div
              className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / (questions.length + 1)) * 100}%` }}
            />
          </div>
        </div>

        {/* Question Step */}
        {isOnQuestionStep && currentQuestion && (
          <Card className="shadow-sm border-slate-200">
            <CardHeader>
              <CardTitle className="text-2xl">{currentQuestion.question_text}</CardTitle>
              <CardDescription>
                Question {currentStep + 1} of {questions.length}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {currentQuestion.question_type === 'yes_no' && (
                <div className="space-y-3">
                  {['Yes', 'No'].map((option) => (
                    <button
                      key={option}
                      onClick={() => handleAnswer(currentQuestion.id, option)}
                      className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
                        answers[currentQuestion.id] === option
                          ? 'border-emerald-600 bg-emerald-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{option}</span>
                        {answers[currentQuestion.id] === option && (
                          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {currentQuestion.question_type === 'multiple_choice' && (
                <div className="space-y-3">
                  {currentQuestion.options.map((option) => (
                    <button
                      key={option}
                      onClick={() => handleAnswer(currentQuestion.id, option)}
                      className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
                        answers[currentQuestion.id] === option
                          ? 'border-emerald-600 bg-emerald-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{option}</span>
                        {answers[currentQuestion.id] === option && (
                          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {currentQuestion.question_type === 'number' && (
                <div className="space-y-2">
                  <Input
                    type="number"
                    value={answers[currentQuestion.id] || ''}
                    onChange={(e) => handleAnswer(currentQuestion.id, parseInt(e.target.value) || 0)}
                    placeholder="Enter number"
                    min="0"
                    className="text-lg p-3"
                  />
                </div>
              )}

              {currentQuestion.question_type === 'text' && (
                <div className="space-y-2">
                  <Input
                    type="text"
                    value={answers[currentQuestion.id] || ''}
                    onChange={(e) => handleAnswer(currentQuestion.id, e.target.value)}
                    placeholder="Enter your answer"
                    className="text-lg p-3"
                  />
                </div>
              )}

              <div className="flex justify-between mt-6 pt-6 border-t border-slate-200">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  disabled={currentStep === 0}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={handleNext}
                  className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800"
                  disabled={!answers[currentQuestion.id]}
                >
                  {currentStep === questions.length - 1 ? 'Review Services' : 'Next'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Service Selection Step */}
        {!isOnQuestionStep && (
          <>
            <Card className="shadow-sm border-slate-200 mb-6">
              <CardHeader>
                <CardTitle>Select Services & Quantities</CardTitle>
                <CardDescription>
                  Choose services and specify the number of users for each
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {services.map((service) => {
                    const isSelected = selectedServices.includes(service.id);
                    const quantity = serviceQuantities[service.id] || service.min_users;
                    const serviceTotal = service.base_price * quantity;

                    return (
                      <div
                        key={service.id}
                        className={`p-4 border-2 rounded-lg transition-all ${
                          isSelected
                            ? 'border-emerald-600 bg-emerald-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleService(service.id)}
                                className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                              />
                              <h3 className="font-semibold text-slate-800">{service.name}</h3>
                            </div>
                            {service.description && (
                              <p className="text-sm text-slate-600 mt-1 ml-6">{service.description}</p>
                            )}
                            <div className="text-sm text-slate-500 mt-2 ml-6">
                              £{service.base_price.toFixed(2)}/user/month
                              {service.min_users > 1 && (
                                <span className="text-xs ml-2">(minimum {service.min_users} users)</span>
                              )}
                            </div>
                          </div>
                          
                          {isSelected && (
                            <div className="flex items-center gap-3">
                              <div className="text-right">
                                <Label htmlFor={`qty-${service.id}`} className="text-xs text-slate-600 mb-1 block">
                                  Users
                                </Label>
                                <Input
                                  id={`qty-${service.id}`}
                                  type="number"
                                  value={quantity}
                                  onChange={(e) => updateServiceQuantity(service.id, e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  min={service.min_users}
                                  className="w-20 text-center"
                                />
                              </div>
                              <div className="text-right">
                                <p className="text-xs text-slate-600 mb-1">Total</p>
                                <p className="font-semibold text-slate-800 text-lg">
                                  £{serviceTotal.toFixed(2)}
                                </p>
                                <p className="text-xs text-slate-500">per month</p>
                              </div>
                            </div>
                          )}
                          
                          {!isSelected && (
                            <div className="text-right text-slate-400">
                              <p className="text-sm">Not selected</p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex justify-between mt-6 pt-6 border-t border-slate-200">
                  <Button
                    variant="outline"
                    onClick={handleBack}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={handleGenerateQuote}
                    className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800"
                    disabled={generating || selectedServices.length === 0}
                  >
                    {generating ? 'Generating...' : 'Generate Quote'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Quote Summary */}
            <Card className="shadow-sm border-slate-200 bg-slate-50">
              <CardHeader>
                <CardTitle>Quote Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {servicesWithQuantity.map((item) => (
                    <div key={item.service_id} className="flex justify-between text-sm">
                      <span className="text-slate-700">
                        {item.service_name} × {item.quantity}
                      </span>
                      <span className="font-medium text-slate-800">
                        £{item.total.toFixed(2)}
                      </span>
                    </div>
                  ))}
                  <div className="pt-3 border-t border-slate-300 flex justify-between text-lg font-semibold">
                    <span>Total per Month</span>
                    <span className="text-emerald-600">£{total.toFixed(2)}</span>
                  </div>
                  <div className="text-xs text-slate-500 text-right">
                    £{(total * 12).toFixed(2)} per year
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
};

export default GenerateQuote;
